import type { SchwarzesBrettFile, SchwarzesBrettFolder, SchwarzesBrettTree } from "@/lib/schwarzesBrett";

export type FileKind = "pdf" | "word" | "excel" | "powerpoint" | "image" | "text" | "other";

export interface FileKindMeta {
  kind: FileKind;
  /** Kurzes Label für Badge/Chip, z. B. „PDF“, „Word“. */
  label: string;
  /** Name der Office-Web-App, in der die Datei online geöffnet wird. */
  officeApp: string | null;
  /** Tailwind-Klassen für das Typ-Badge. */
  badgeClass: string;
}

const WORD = new Set(["doc", "docx", "docm", "dot", "dotx", "odt", "rtf"]);
const EXCEL = new Set(["xls", "xlsx", "xlsm", "xlsb", "csv", "ods"]);
const POWERPOINT = new Set(["ppt", "pptx", "pptm", "pps", "ppsx", "odp"]);
const IMAGE = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "heic", "tif", "tiff"]);
const TEXT = new Set(["txt", "md", "htm", "html", "eml", "msg"]);

export function fileKindMeta(extension: string | null | undefined): FileKindMeta {
  const ext = String(extension || "").toLowerCase();
  if (ext === "pdf") {
    return { kind: "pdf", label: "PDF", officeApp: null, badgeClass: "bg-status-red/15 text-status-red" };
  }
  if (WORD.has(ext)) {
    return { kind: "word", label: "Word", officeApp: "Word", badgeClass: "bg-accent-soft text-accent" };
  }
  if (EXCEL.has(ext)) {
    return { kind: "excel", label: "Excel", officeApp: "Excel", badgeClass: "bg-status-green/15 text-status-green" };
  }
  if (POWERPOINT.has(ext)) {
    return {
      kind: "powerpoint",
      label: "PowerPoint",
      officeApp: "PowerPoint",
      badgeClass: "bg-status-orange/15 text-status-orange",
    };
  }
  if (IMAGE.has(ext)) {
    return { kind: "image", label: "Bild", officeApp: null, badgeClass: "bg-fill-tertiary text-label-secondary" };
  }
  if (TEXT.has(ext)) {
    return { kind: "text", label: ext.toUpperCase(), officeApp: null, badgeClass: "bg-fill-tertiary text-label-secondary" };
  }
  return {
    kind: "other",
    label: ext ? ext.toUpperCase() : "Datei",
    officeApp: null,
    badgeClass: "bg-fill-tertiary text-label-secondary",
  };
}

/** Datei in den letzten `days` Tagen geändert? */
export function isRecentFile(
  file: Pick<SchwarzesBrettFile, "last_modified_at">,
  days: number,
  now: Date = new Date(),
): boolean {
  if (!file.last_modified_at || days <= 0) return false;
  const stamp = new Date(file.last_modified_at).getTime();
  if (Number.isNaN(stamp)) return false;
  return now.getTime() - stamp <= days * 24 * 60 * 60 * 1000;
}

export function countRecentFiles(
  files: Array<Pick<SchwarzesBrettFile, "last_modified_at">>,
  days: number,
  now: Date = new Date(),
): number {
  return files.reduce((acc, f) => (isRecentFile(f, days, now) ? acc + 1 : acc), 0);
}

/** Dateiname ohne Endung – für die Anzeige, wenn das Typ-Badge die Endung schon zeigt. */
export function displayFileName(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot >= name.length - 1) return name;
  return name.slice(0, dot);
}

function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss");
}

/** Alle Dateien des Baums (inkl. loser Dateien), optional mit Suchfilter. */
export function searchFiles(tree: SchwarzesBrettTree, query: string): SchwarzesBrettFile[] {
  const all: SchwarzesBrettFile[] = [
    ...tree.folders.flatMap((f) => f.files),
    ...tree.loose_files,
  ];
  const terms = normalize(query.trim()).split(/\s+/).filter(Boolean);
  if (!terms.length) return all;
  return all.filter((file) => {
    const haystack = normalize(
      [file.name, file.folder_name || "", file.sub_path || ""].join(" "),
    );
    return terms.every((term) => haystack.includes(term));
  });
}

/** Ordner mit Trefferzahl für die Suchergebnis-Gruppierung. */
export function groupByFolder(
  files: SchwarzesBrettFile[],
  folders: SchwarzesBrettFolder[],
): Array<{ folder: SchwarzesBrettFolder | null; files: SchwarzesBrettFile[] }> {
  const byId = new Map<string, SchwarzesBrettFile[]>();
  const loose: SchwarzesBrettFile[] = [];
  for (const file of files) {
    if (!file.folder_id) {
      loose.push(file);
      continue;
    }
    const list = byId.get(file.folder_id) || [];
    list.push(file);
    byId.set(file.folder_id, list);
  }
  const groups: Array<{ folder: SchwarzesBrettFolder | null; files: SchwarzesBrettFile[] }> = folders
    .filter((folder) => byId.has(folder.id))
    .map((folder) => ({ folder, files: byId.get(folder.id) || [] }));
  if (loose.length) groups.push({ folder: null, files: loose });
  return groups;
}
